import { useEffect, useRef, useState } from "react";
import { AuthCard } from "@/components/shell/auth-card";
import { Button } from "@/components/ui/button";
import { FullPageError } from "@/components/ui/system-state";
import { useAuthStore } from "@/stores/auth-store";
import {
  acceptAdminInvite,
  fetchAuthOptions,
  previewAdminInvite,
  type AdminInvitePreviewResult,
} from "@/lib/api";

// ---------------------------------------------------------------------------
// /invite/accept (ALL-4851)
//
// Reached from the invite email built in context#994
// (InviteEmailTemplate.acceptUrl): "<ALLOY_INVITE_ACCEPT_URL_BASE>/invite/
// accept#token=<rawInviteToken>". The token sits in the URL **fragment**
// (`#token=...`), not a query parameter — context#994 chose the fragment
// specifically so the token never reaches server logs or a Referer header.
// This page:
//   1. Reads the token from the fragment only, once, on mount.
//   2. Never logs it (no console.*, no analytics event carries it).
//   3. Moves it into sessionStorage and strips the fragment via
//      history.replaceState before anything else runs, so a reload or the
//      back button never re-exposes it in the visible URL/history.
//   4. Carries it through the Google OAuth round trip via that same
//      sessionStorage entry (not the URL) — see the GAP note in
//      src/lib/api.ts above previewAdminInvite/acceptAdminInvite: the
//      backend documents no OAuth invite-carry mechanism for admin invites
//      today (only bootstrap invites have a browser-session accept path),
//      so sessionStorage is the least-invasive choice consistent with how
//      this app already does provider redirects (fetchAuthOptions/
//      startUrl?return_to=...), not an invented protocol.
// ---------------------------------------------------------------------------

const TOKEN_STORAGE_KEY = "alloy.invite.accept.token";

type PageState =
  | "resolving"
  | "need-signin"
  | "signing-in"
  | "accepting"
  | "success"
  | "error";

type ErrorCategory = "expired" | "used" | "invalid" | "wrong_account" | "generic";

function categorizeReasonCode(reasonCode?: string): ErrorCategory {
  if (!reasonCode) return "generic";
  const code = reasonCode.toUpperCase();
  // ALL-5143: the server refuses an accept from a session whose email is not the
  // invited email with AUTH_RESOURCE_OWNERSHIP_MISMATCH. That is the wrong-account
  // case (sign out, continue with the invited account), never a generic failure.
  if (code.includes("OWNERSHIP_MISMATCH")) return "wrong_account";
  if (code.includes("EXPIRED")) return "expired";
  if (code.includes("ALREADY") || code.includes("ACCEPTED") || code.includes("USED")) return "used";
  if (code.includes("MALFORMED") || code.includes("INVALID") || code.includes("UNKNOWN_CREDENTIAL")) return "invalid";
  return "generic";
}

function GoogleIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="var(--ads-brand-google-blue)" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="var(--ads-brand-google-green)" />
      <path d="M5.84 14.09A6.68 6.68 0 0 1 5.5 12c0-.72.13-1.43.34-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.93l3.66-2.84z" fill="var(--ads-brand-google-yellow)" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="var(--ads-brand-google-red)" />
    </svg>
  );
}

/**
 * Where to land after a successful accept (ALL-5137). A transaction_signer
 * goes to the signer Get started page (install the app, ask the admin to pair,
 * create the wallet); everyone else keeps landing on /wallets. The role comes
 * from the refreshed live session, never guessed client-side.
 */
export function postAcceptPath(
  roleNames: readonly string[] | null | undefined,
  invitedRoleName?: string | null,
): string {
  // ALL-5143: the session's roleNames are fixed at sign-in, so right after an
  // accept they usually do not yet include the new role. The invite's own role
  // (from the preview) decides the landing; the live session is the fallback.
  if (invitedRoleName === "transaction_signer") return "/signer/get-started";
  return Array.isArray(roleNames) && roleNames.includes("transaction_signer") ? "/signer/get-started" : "/wallets";
}

/** Reads and permanently removes the one-time invite token from the URL fragment, if present. */
function consumeTokenFromFragment(): string | null {
  const hash = window.location.hash;
  if (!hash || hash.length < 2) return null;
  const params = new URLSearchParams(hash.slice(1));
  const token = params.get("token");
  // Strip the fragment immediately, regardless of whether it parsed to a
  // token, so a malformed fragment doesn't linger in history either.
  window.history.replaceState(null, "", window.location.pathname + window.location.search);
  return token && token.trim() ? token : null;
}

export default function InviteAcceptPage() {
  const step = useAuthStore((s) => s.step);
  const sessionUser = useAuthStore((s) => s.user);
  const initSession = useAuthStore((s) => s.initSession);
  const signOut = useAuthStore((s) => s.signOut);

  const [pageState, setPageState] = useState<PageState>("resolving");
  const [preview, setPreview] = useState<AdminInvitePreviewResult | null>(null);
  const [errorCategory, setErrorCategory] = useState<ErrorCategory>("generic");
  const [reasonCode, setReasonCode] = useState<string | undefined>(undefined);
  const [googleStartUrl, setGoogleStartUrl] = useState<string | null>(null);

  const tokenRef = useRef<string | null>(null);
  const acceptStartedRef = useRef(false);

  // Step 1: resolve the token (fragment first, then sessionStorage for a
  // page reload or an OAuth-round-trip return) exactly once.
  useEffect(() => {
    const fromFragment = consumeTokenFromFragment();
    if (fromFragment) {
      try {
        window.sessionStorage.setItem(TOKEN_STORAGE_KEY, fromFragment);
      } catch {
        // sessionStorage unavailable (private mode, etc.) — fall through and
        // keep the token in memory only for this page life; the OAuth round
        // trip simply won't survive in that case, which is a browser
        // limitation, not a bug in this page.
      }
      tokenRef.current = fromFragment;
    } else {
      try {
        tokenRef.current = window.sessionStorage.getItem(TOKEN_STORAGE_KEY);
      } catch {
        tokenRef.current = null;
      }
    }

    if (!tokenRef.current) {
      setErrorCategory("invalid");
      setPageState("error");
      return;
    }

    previewAdminInvite(tokenRef.current).then((result) => {
      if (result?.reasonCode) {
        setReasonCode(result.reasonCode);
        setErrorCategory(categorizeReasonCode(result.reasonCode));
        setPageState("error");
        return;
      }
      // A failed/absent preview is never fatal — the admin-invite preview
      // endpoint does not exist on the backend yet (see the GAP note in
      // src/lib/api.ts). Sign-in still proceeds; we simply have no
      // organization/inviter to show ahead of it.
      setPreview(result);
      setPageState("need-signin");
    });
  }, []);

  // Fetch the configured Google sign-in start URL once we know we need it.
  useEffect(() => {
    if (pageState !== "need-signin") return;
    let cancelled = false;
    fetchAuthOptions("/invite/accept").then((methods) => {
      if (cancelled) return;
      const google = methods.find((m) => /google/i.test(m.label) || m.id === "google-oidc");
      setGoogleStartUrl(google?.startUrl || "/auth/login?return_to=" + encodeURIComponent("/invite/accept"));
    });
    return () => {
      cancelled = true;
    };
  }, [pageState]);

  // Step 2: once Google sign-in returns (auth store step flips to "app"),
  // check for a wrong-account mismatch, then accept.
  useEffect(() => {
    if (step !== "app" || !tokenRef.current || acceptStartedRef.current) return;
    if (pageState === "success" || pageState === "error") return;
    // ALL-5143: never accept before the preview has resolved. A browser that is
    // already signed in reaches step "app" immediately; accepting before the
    // preview arrives skipped the wrong-account check below and sent the accept
    // with whichever account happened to be signed in.
    if (pageState === "resolving") return;

    const invitedEmail = preview?.email?.toLowerCase().trim();
    const signedInEmail = sessionUser?.email?.toLowerCase().trim();
    if (invitedEmail && signedInEmail && invitedEmail !== signedInEmail) {
      setErrorCategory("wrong_account");
      setPageState("error");
      return;
    }

    acceptStartedRef.current = true;
    setPageState("accepting");
    acceptAdminInvite(tokenRef.current).then((result) => {
      if (!result.ok) {
        const category = categorizeReasonCode(result.reasonCode);
        // Keep the one-time token on a wrong-account refusal: the invite is
        // still open, and the person continues here with the invited account.
        if (category !== "wrong_account") {
          try {
            window.sessionStorage.removeItem(TOKEN_STORAGE_KEY);
          } catch {
            // ignore
          }
        }
        setReasonCode(result.reasonCode);
        setErrorCategory(category);
        setPageState("error");
        return;
      }
      try {
        window.sessionStorage.removeItem(TOKEN_STORAGE_KEY);
      } catch {
        // ignore
      }
      setPageState("success");
      // Refresh the session/role context the resolved principal now has,
      // then route into the app. initSession() re-derives `user.role` from
      // the live session, so we never guess the role client-side.
      initSession().then(() => {
        window.location.assign(postAcceptPath(useAuthStore.getState().session?.roleNames, preview?.roleName));
      });
    });
  }, [step, pageState, preview, sessionUser, initSession]);

  function retryDifferentAccount() {
    // Back to this page (the token is still in sessionStorage), so the person
    // signs in with the invited Google account and the accept completes.
    signOut().then(() => {
      window.location.assign("/invite/accept");
    });
  }

  // A token-level problem (missing/expired/used/invalid/wrong-account) must
  // win over the auth store's own "loading" step — the store may still be
  // bootstrapping the session in the background, but there is nothing to
  // wait for once we already know the invite itself can't be accepted.
  if (pageState !== "error" && (pageState === "resolving" || step === "loading")) {
    return (
      <AuthCard>
        <div className="flex w-full items-center justify-center py-8">
          <div className="spinner" aria-label="Checking your invitation" />
        </div>
      </AuthCard>
    );
  }

  if (pageState === "error") {
    const copy: Record<ErrorCategory, { code: string; title: string; description: string }> = {
      expired: {
        code: "410",
        title: "This invitation has expired",
        description:
          "Invitations are time-limited. Ask your organization admin to send a new invite, then use the link in that email.",
      },
      used: {
        code: "409",
        title: "This invitation was already used",
        description:
          "It looks like this invite link has already been accepted. Sign in normally, or ask your admin for a new invite if you still need access.",
      },
      invalid: {
        code: "400",
        title: "This invitation link isn't valid",
        description:
          "The invitation link is missing or malformed. Copy the link from your invite email again, or ask your organization admin to resend it.",
      },
      wrong_account: {
        code: "403",
        title: "Signed in with a different account",
        description: preview?.email
          ? `This invite was sent to ${preview.email}. Sign out and continue with the Google account that matches that address.`
          : "This invite was sent to a different Google account than the one you're signed in with. Sign out and try again with the invited account.",
      },
      generic: {
        code: "500",
        title: "We couldn't complete your invitation",
        description: "Something went wrong while accepting this invitation. You can try again; if it persists, contact support.",
      },
    };
    const { code, title, description } = copy[errorCategory];
    return (
      <div className="w-full max-w-[640px]">
        <FullPageError
          code={code}
          codeTone={errorCategory === "wrong_account" ? "warning" : "danger"}
          title={title}
          description={description}
          incident={reasonCode}
          actions={
            errorCategory === "wrong_account" ? (
              <Button variant="secondary" onClick={retryDifferentAccount}>
                Sign out and try another account
              </Button>
            ) : undefined
          }
          homeHref="/login"
          homeLabel="Go to sign in"
        />
      </div>
    );
  }

  if (pageState === "accepting" || pageState === "success") {
    return (
      <AuthCard>
        <div className="auth-heading">
          <h1 className="auth-title">Finishing up</h1>
          <p className="auth-subtitle">Setting up your access&hellip;</p>
        </div>
        <div className="flex w-full items-center justify-center py-8">
          <div className="spinner" aria-label="Completing invitation acceptance" />
        </div>
      </AuthCard>
    );
  }

  // pageState === "need-signin"
  return (
    <AuthCard>
      <div className="auth-heading">
        <h1 className="auth-title">You&apos;re invited to Alloy</h1>
        {preview?.organizationName ? (
          <p className="auth-subtitle">
            {preview.inviterName ? `${preview.inviterName} has` : "You've been"} invited you to join{" "}
            <strong>{preview.organizationName}</strong>.
          </p>
        ) : (
          <p className="auth-subtitle">Sign in with Google to accept your invitation.</p>
        )}
        {preview?.inviterEmail && (
          <p className="auth-subtitle" style={{ fontSize: 13 }}>
            Invited by {preview.inviterEmail}
          </p>
        )}
      </div>

      <div className="auth-block">
        <Button
          variant="ghost"
          size="lg"
          className="w-full justify-center gap-2 bg-bg hover:bg-disabled-soft focus-visible:bg-bg disabled:bg-bg"
          loading={!googleStartUrl}
          aria-label="Continue with Google"
          onClick={() => {
            if (!googleStartUrl) return;
            setPageState("signing-in");
            window.location.assign(googleStartUrl);
          }}
        >
          <GoogleIcon size={20} />
          Continue with Google
        </Button>
      </div>
    </AuthCard>
  );
}
